import * as React from "react";

import { BreadcrumbTemplateEnum, Breadcrumbs, LoadingSpinner, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { UserInfo } from "@app/user";
import { UserSessionService } from "./userSessionService";
import { UserSession } from "./apiModels";
import { JSX } from "react";

interface UserSessionsProps {
  user: UserInfo;
}

interface UserSessionsState {
  loaded: boolean;
  selectedSessions: UserSession[];
  userSessions: UserSession[];
}

interface UserSessionRecord {
  clientName: string;
  key: string;
  name: string;
  sessionStartDate: string | Date;
  userEmail: string;
  username: string;
}

class UserSessions extends React.Component<UserSessionsProps, UserSessionsState> {
  private readonly columnConfig: ColumnConfig;
  private readonly userSessionService: UserSessionService;

  constructor(props: UserSessionsProps) {
    super(props);

    this.state = {
      loaded: false,
      selectedSessions: [],
      userSessions: [],
    };

    this.columnConfig = {
      clientName: {
        displayText: "Client",
        testId: "client-name",
      },
      name: {
        displayText: "Name",
        testId: "name",
      },
      sessionStartDate: {
        displayText: "Session Start Date",
        testId: "session-start-date",
      },
      userEmail: {
        displayText: "Email",
        testId: "user-email",
      },
      username: {
        displayText: "Username",
        testId: "username",
      },
    };

    this.userSessionService = new UserSessionService();
  }

  public async componentDidMount() {
    // filter out the current user session, we may have to undo this later
    // if so, we'll need be sure to either stop the user from potentially terminating
    // their own current session...or notify them that they've selected their own
    // session for termination
    const userSessions = (await this.userSessionService.get())
      .filter((session) => session.userId !== this.props.user.userId);

    this.setState((prevState) => ({
      ...prevState,
      loaded: true,
      userSessions,
    }));
  }

  public render() {
    return (
      <Page documentTitle="User Sessions - Admin">
        <Breadcrumbs
          template={BreadcrumbTemplateEnum.Admin}
          items={[
            {
              iconClass: "fa-clock-o",
              text: "User Sessions",
            },
          ]}
        />
        <PageInner>
          <PageHeader title="User Sessions" />
          <PageSection>
            <div className="row mt-3">
              <div className="col-sm-12">
                {!this.state.loaded ? (<LoadingSpinner />) : this.renderRecords()}
              </div>
            </div>
          </PageSection>
        </PageInner>
      </Page>
    );
  }

  public renderRecords(): JSX.Element {
    /* eslint-disable sort-keys */
    const records: UserSessionRecord[] = this.state.userSessions.map((s) => {
      return {
        [wafflesKeyColumnName]: s.sessionId,
        name: s.userDisplayName,
        username: s.username,
        userEmail: s.userEmail,
        clientName: s.clientName,
        sessionStartDate: this.formatLocalDate(s.sessionStartDate),
      };
    });
    /* eslint-enable sort-keys */
    const selectedKeys = this.state.selectedSessions.map((session) => session.sessionId);
    const showBulkAction = selectedKeys.length > 0;

    const bulkActionBar = (
      <span>
        {showBulkAction
          ? (<a href="#" onClick={this.handleEndSessionClick}><i className="fa fa-ban" /> End Session</a>)
          : (<span>&nbsp;</span>)}
      </span>
    );

    return (
      <>
        <h5 className="mb-0">Active Sessions</h5>
        <Waffles<UserSessionRecord>
          bulkActionBar={bulkActionBar}
          columns={this.columnConfig}
          data={records}
          fullWidth={true}
          handleCheckboxChange={this.handleCheckboxChange}
          selectedKeys={selectedKeys}
          showCheckboxes={true}
          testId="user-session-table"
        />
      </>
    );
  }

  private formatLocalDate(dateToFormat: Date): string {
    const formattedDate = new Date(dateToFormat.toString());
    return `${formattedDate.toLocaleDateString()} ${formattedDate.toLocaleTimeString()}`;
  }

  private handleCheckboxChange = (selectedKeys: string[]) => {
    const selectedSessions = this.state.userSessions.filter((session) => selectedKeys.includes(session.sessionId));

    this.setState((prevState) => ({
      ...prevState,
      selectedSessions,
    }));
  }

  private handleEndSessionClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (this.state.selectedSessions.length > 0) {
      await this.userSessionService.patch(this.state.selectedSessions);
      const userSessions = this.state.userSessions.filter((session) => !this.state.selectedSessions.includes(session));
      this.setState((prevState): UserSessionsState => ({
        ...prevState,
        selectedSessions: [],
        userSessions,
      }));
    }
  }
}

export {
  UserSessions,
};
